import { ChatComposer } from '@/components/chat/ChatComposer';
import { useChatScroll } from '@/components/chat/useChatScroll';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RefreshCw, CheckCircle2, AlertTriangle, XCircle,
  Play, Loader2, // reuse-ok: spinner chico dentro de botones y burbujas del chat, ya estaba así
  Bot, Send, ChevronDown, ChevronUp, Clock,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { BRAIN_CLIENTS } from '../../../../shared/types/brain';
import {
  BRAIN_HEALTH_SOURCES, BRAIN_SYNC_IDS, brainSourceStatsColumn, type BrainSyncId,
  BRAIN_SYNCS, brainSyncFreshness, brainSourceOrManual, type BrainSyncFreshness,
} from '../../../../shared/brain-sources';
import type { PmlCoverageStatus } from '../../../../shared/brain-pml-coverage';
import { BRAIN_SOURCE_LABEL_KEYS, BRAIN_SYNC_LABEL_KEYS, brainSourceUi } from '@/components/brain/brain-source-ui';

// Fuentes, syncs y clientes salen de los registros de shared/ (brain-sources.ts,
// brain-clients/): una fuente nueva aparece acá sola, sin tocar este archivo.

function formatRelative(iso: string, t: ReturnType<typeof useTranslation>['t']): string {
  const diff  = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (mins < 60)  return t('brain.health.freshness.mins_ago', { count: mins });
  if (hours < 24) return t('brain.health.freshness.hours_ago', { count: hours });
  return t('brain.health.freshness.days_ago', { count: days });
}

/**
 * Semáforo de frescura. Dos modos:
 *  - sin `state`: el de siempre, por antigüedad del dato de un cliente (25 h / 72 h).
 *  - con `state`: el que decide `brainSyncFreshness` con los umbrales por sync de
 *    shared/brain-sources.ts (05/10/2026). Es un solo componente a propósito: que el
 *    verde y el rojo signifiquen lo mismo en toda la tab.
 */
function FreshnessBadge({ lastUpdated, state }: { lastUpdated: string | null; state?: BrainSyncFreshness }) {
  const { t } = useTranslation();
  if (state) {
    const label = lastUpdated ? formatRelative(lastUpdated, t) : t('brain.health.no_data');
    const estilo: Record<BrainSyncFreshness, string> = {
      ok:            'bg-emerald-50 text-emerald-700 border-emerald-200',
      warn:          'bg-amber-50 text-amber-700 border-amber-200',
      stale:         'bg-red-50 text-red-700 border-red-200',
      unknown:       'bg-gray-100 text-gray-500 border-gray-200',
      'not-watched': 'bg-gray-100 text-gray-500 border-gray-200',
    };
    const Icono = state === 'ok' ? CheckCircle2 : state === 'warn' ? AlertTriangle : state === 'stale' ? XCircle : Clock;
    return (
      <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border font-medium ${estilo[state]}`}>
        <Icono className="w-3.5 h-3.5" /> {label}
      </span>
    );
  }
  if (!lastUpdated) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 border border-gray-200 font-medium">
        <XCircle className="w-3.5 h-3.5" /> {t('brain.health.no_data')}
      </span>
    );
  }
  const ageHours = (Date.now() - new Date(lastUpdated).getTime()) / 3_600_000;
  const label = formatRelative(lastUpdated, t);

  if (ageHours < 25) return (
    <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
      <CheckCircle2 className="w-3.5 h-3.5" /> {label}
    </span>
  );
  if (ageHours < 72) return (
    <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">
      <AlertTriangle className="w-3.5 h-3.5" /> {label}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200 font-medium">
      <XCircle className="w-3.5 h-3.5" /> {label}
    </span>
  );
}

function SyncButton({ clientId, source, label }: { clientId: string; source: BrainSyncId; label: string }) {
  const { t } = useTranslation();
  const [done, setDone] = useState(false);
  const trigger = trpc.brain.triggerClientSync.useMutation({
    onSuccess: () => { setDone(true); setTimeout(() => setDone(false), 4000); },
  });

  return (
    <button
      onClick={() => trigger.mutate({ clientId, source })}
      disabled={trigger.isPending || done}
      className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border font-medium transition-all disabled:opacity-50 ${
        done
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300'
      }`}
    >
      {trigger.isPending
        ? <Loader2 className="w-3 h-3 animate-spin" />
        : done
          ? <CheckCircle2 className="w-3 h-3" />
          : <Play className="w-3 h-3" />
      }
      {done ? t('brain.health.sync_started') : label}
    </button>
  );
}

/**
 * Perfiles PML (T4, 19/10/2026): el semáforo por perfil que deja el cron de cobertura
 * (shared/brain-pml-coverage.ts) y sus señales. Mismo FreshnessBadge que las fuentes para
 * que el verde y el rojo signifiquen lo mismo en toda la tab. Sin perfiles, no se dibuja.
 */
const COVERAGE_STATE: Record<PmlCoverageStatus, BrainSyncFreshness> = { ok: 'ok', warn: 'warn', error: 'stale' };

function PmlCoverageSection() {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = trpc.brain.pmlCoverage.useQuery(undefined, { staleTime: 60_000 });
  const [done, setDone] = useState(false);
  const trigger = trpc.brain.triggerPmlCoverage.useMutation({
    onSuccess: () => { setDone(true); setTimeout(() => { setDone(false); refetch(); }, 20_000); },
  });
  if (isLoading || !data || data.reports.length === 0) return null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">{t('brain.health.pml_title')}</p>
          <p className="text-xs text-gray-400">{t('brain.health.pml_hint')}</p>
        </div>
        {data.canTrigger && (
          <button
            onClick={() => trigger.mutate(undefined)}
            disabled={trigger.isPending || done}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border font-medium bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50"
          >
            {trigger.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : done ? <CheckCircle2 className="w-3 h-3" /> : <Play className="w-3 h-3" />} {/* reuse-ok: mismo spinner chico en botón que SyncButton, en esta misma tab */}
            {done ? t('brain.health.pml_recalc_started') : t('brain.health.pml_recalc')}
          </button>
        )}
      </div>
      <div className="space-y-2">
        {data.reports.map((r) => (
          <div key={r.profileId} className="rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{r.name}</p>
                <p className="text-[11px] text-gray-400">
                  {r.generatedAt ? t('brain.health.pml_signals', { count: r.signals.length }) : t('brain.health.pml_no_report')}
                </p>
              </div>
              <FreshnessBadge lastUpdated={r.generatedAt || null} state={r.generatedAt ? COVERAGE_STATE[r.status] : 'unknown'} />
            </div>
            {r.signals.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs">
                {r.signals.map((s) => (
                  <li key={s.key} className={s.severity === 'error' ? 'text-red-700' : 'text-amber-700'}>• {s.text}</li>
                ))}
              </ul>
            )}
            {r.sources.length > 0 && (
              <p className="mt-1 text-[11px] text-gray-400">
                {r.sources.map((s) => `${t(BRAIN_SOURCE_LABEL_KEYS[brainSourceOrManual(s.source)])} ${s.n}`).join(' · ')}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

type ClientRow = Record<string, string | number | boolean | null>;

function ClientCard({ c }: { c: ClientRow }) {
  const { t } = useTranslation();
  const clientId   = c.client_id as string;
  const pageCount  = Number(c.page_count);
  const chunkCount = Number(c.chunk_count);
  const lastUpdated = c.last_updated as string | null;
  const chunksPerPage = pageCount > 0 ? (chunkCount / pageCount).toFixed(1) : '—';

  return (
    <div className="border rounded-xl p-4 space-y-4 bg-white hover:shadow-xs transition-shadow">
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-gray-900 text-base leading-tight">
            {BRAIN_CLIENTS[clientId] ?? clientId}
          </p>
          <p className="text-xs text-gray-400 font-mono mt-0.5">{clientId}</p>
        </div>
        <FreshnessBadge lastUpdated={lastUpdated} />
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gray-50 rounded-lg px-3 py-2 text-center">
          <p className="text-xl font-bold text-gray-900">{pageCount.toLocaleString()}</p>
          <p className="text-xs text-gray-500 mt-0.5">{t('brain.health.stats.pages')}</p>
        </div>
        <div className="bg-gray-50 rounded-lg px-3 py-2 text-center">
          <p className="text-xl font-bold text-gray-900">{chunkCount.toLocaleString()}</p>
          <p className="text-xs text-gray-500 mt-0.5">{t('brain.health.stats.chunks')}</p>
        </div>
        <div className="bg-gray-50 rounded-lg px-3 py-2 text-center">
          <p className="text-xl font-bold text-gray-900">{chunksPerPage}</p>
          <p className="text-xs text-gray-500 mt-0.5">{t('brain.health.stats.chunks_per_page')}</p>
        </div>
      </div>

      {/* Sources breakdown */}
      <div>
        <p className="text-xs font-medium text-gray-500 mb-2">{t('brain.health.stats.data_sources')}</p>
        <div className="grid grid-cols-2 gap-2">
          {BRAIN_HEALTH_SOURCES.map((sourceId) => {
            const { Icon, color, bg } = brainSourceUi(sourceId);
            const count = Number(c[brainSourceStatsColumn(sourceId)] ?? 0);
            return (
              <div key={sourceId} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${bg} ${count === 0 ? 'opacity-40' : ''}`}>
                <Icon className={`w-3.5 h-3.5 shrink-0 ${color}`} />
                <div className="min-w-0">
                  <p className={`text-xs font-semibold ${color}`}>{t(BRAIN_SOURCE_LABEL_KEYS[sourceId])}</p>
                  <p className="text-xs text-gray-600">
                    {count > 0
                      ? t('brain.health.stats.pages_count', { count })
                      : t('brain.health.stats.no_source_data')}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sync buttons */}
      <div>
        <p className="text-xs font-medium text-gray-500 mb-2">{t('brain.health.force_sync')}</p>
        <div className="flex flex-wrap gap-1.5">
          {BRAIN_SYNC_IDS.map((sync) => (
            <SyncButton key={sync} clientId={clientId} source={sync} label={t(BRAIN_SYNC_LABEL_KEYS[sync])} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Chunk backfill ───────────────────────────────────────────────────────────

function ChunkBackfillButton() {
  const { t } = useTranslation();
  const [done, setDone] = useState(false);
  const trigger = trpc.brain.triggerChunkBackfill.useMutation({
    onSuccess: () => { setDone(true); setTimeout(() => setDone(false), 6000); },
  });

  return (
    <button
      onClick={() => trigger.mutate({})}
      disabled={trigger.isPending || done}
      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border font-medium transition-all disabled:opacity-50 ${
        done
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900'
      }`}
    >
      {trigger.isPending
        ? <Loader2 className="w-3 h-3 animate-spin" />
        : done
          ? <CheckCircle2 className="w-3 h-3" />
          : <Play className="w-3 h-3" />
      }
      {done ? t('brain.health.chunks_started') : t('brain.health.generate_chunks_btn')}
    </button>
  );
}

// ─── Admin Agent ──────────────────────────────────────────────────────────────

interface ChatMessage { role: 'user' | 'assistant'; text: string }

function AdminAgentPanel() {
  const { t } = useTranslation();
  const [open, setOpen]     = useState(false);
  const [input, setInput]   = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const chat = trpc.brain.adminChat.useMutation({
    onSuccess: (data) => {
      setMessages((m) => [...m, { role: 'assistant', text: data.reply }]);
    },
    onError: (err) => {
      setMessages((m) => [...m, { role: 'assistant', text: `Error: ${err.message}` }]);
    },
  });
  const bottomRef = useChatScroll([messages]);

  const send = () => {
    const text = input.trim();
    if (!text || chat.isPending) return;
    setMessages((m) => [...m, { role: 'user', text }]);
    setInput('');
    chat.mutate({ message: text });
  };

  return (
    <div className="border rounded-xl overflow-hidden bg-white">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-[#FF6B6B]" />
          <span>{t('brain.health.agent_title')}</span>
          <span className="text-xs font-normal text-gray-400">{t('brain.health.agent_subtitle')}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {open && (
        <div className="border-t">
          <div className="h-48 overflow-y-auto p-3 space-y-3 bg-gray-50">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full gap-2 text-center">
                <Bot className="w-8 h-8 text-gray-200" />
                <p className="text-xs text-gray-400">
                  {t('brain.health.ejemplos_consultas')}
                </p>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] text-xs px-3 py-2 rounded-xl leading-relaxed whitespace-pre-wrap ${
                  msg.role === 'user'
                    ? 'bg-[#FF6B6B] text-white'
                    : 'bg-white border border-gray-200 text-gray-800'
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {chat.isPending && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-xl px-3 py-2">
                  <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <ChatComposer
            className="p-2 border-t bg-white"
            value={input} onChange={setInput} onSend={send} pending={chat.isPending}
            placeholder={t('brain.health.agent_placeholder')}
            inputClassName="h-8 text-xs rounded-lg focus-visible:ring-[#FF6B6B]/50"
            buttonClassName="h-8 w-8 rounded-lg bg-[#FF6B6B] text-white hover:bg-[#e85d5d]"
            icon={<Send className="w-3.5 h-3.5" />}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Una línea del bloque de blindaje: "<qué falta> (N): a, b, c (+12). <por qué importa>".
 *
 * Estaba copiado cuatro veces con su `slice(0, 12)` y su `(+N)` a mano. Como caja de
 * advertencia hay UNA sola (el bloque ámbar); lo que se repite es la línea.
 */
function FaltanteLinea({ etiqueta, items, hint }: { etiqueta: string; items: string[]; hint: string }) {
  if (!items.length) return null;
  const TOPE = 12;
  return (
    <p className="text-amber-900">
      <span className="font-medium">{etiqueta}:</span>{' '}
      {items.slice(0, TOPE).join(', ')}
      {items.length > TOPE ? ` (+${items.length - TOPE})` : ''}. <span className="text-amber-800">{hint}</span>
    </p>
  );
}

export function BrainHealthTab() {
  const { t } = useTranslation();
  // 02/10/2026: 5 min (era 60 s: un admin con la tab abierta generaba 60 llamadas/hora); el botón refresca a mano.
  const { data, isLoading, refetch, isRefetching } = trpc.brain.adminStats.useQuery(
    undefined,
    { refetchInterval: 5 * 60_000, staleTime: 60_000 }
  );

  const clients = data?.clients ?? [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold text-gray-900">{t('brain.health.kb_status')}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {data?.generatedAt
              ? t('brain.health.updated', { time: formatRelative(data.generatedAt, t) })
              : t('brain.health.loading_stats')}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isRefetching}
          className="p-1.5 text-gray-400 hover:text-gray-700 transition-colors disabled:opacity-50 rounded-md hover:bg-gray-100"
          title={t('brain.health.refresh_now')}
        >
          <RefreshCw className={`w-4 h-4 ${isRefetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Estado de las fuentes: última corrida COMPLETA de cada sync (05/10/2026).
          Es distinto de la frescura por cliente de las cards de abajo: ahí se ve
          cuándo cambió el DATO; acá, cuándo corrió el SYNC. Una fuente muerta tiene
          el dato viejo y la corrida vieja; una fuente sin novedades, solo el dato. */}
      {!isLoading && data && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-baseline justify-between gap-3 mb-3">
            <p className="text-sm font-semibold text-gray-900">{t('brain.health.sources_title')}</p>
            <p className="text-xs text-gray-400">{t('brain.health.sources_hint')}</p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {BRAIN_SYNC_IDS.map((sync) => {
              const lastRun = data.syncLastRuns?.[sync] ?? null;
              const estado  = brainSyncFreshness(sync, lastRun, Date.now());
              return (
                <div key={sync} className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{t(BRAIN_SYNC_LABEL_KEYS[sync])}</p>
                    <p className="text-[11px] text-gray-400">
                      {BRAIN_SYNCS[sync].cadence === 'daily'
                        ? t('brain.health.cadence_daily')
                        : t('brain.health.cadence_on_deploy')}
                    </p>
                  </div>
                  <FreshnessBadge lastUpdated={lastRun} state={estado} />
                </div>
              );
            })}
          </div>
          {data.syncRunsError && (
            <p className="text-xs text-amber-600 mt-3">{t('brain.health.sources_error')}</p>
          )}
        </div>
      )}

      {/* Perfiles PML (T4): cobertura por perfil, del cron pml-coverage. Solo si hay perfiles. */}
      {!isLoading && data && <PmlCoverageSection />}

      {isLoading && (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('brain.health.loading_stats')}
        </div>
      )}

      {!isLoading && clients.length === 0 && (
        <div className="text-center py-12 border rounded-xl border-dashed text-sm text-gray-400">
          {t('brain.health.no_data_yet')}
        </div>
      )}

      {/* Blindaje (09/2026): clientes sin BigQuery y canales donde el bot no está.
          05/10/2026: más los clientes configurados SIN información indexada. */}
      {data && ((data.bqMissing?.length ?? 0) > 0 || (data.hubspotMissing?.length ?? 0) > 0 || (data.slackNotMember?.length ?? 0) > 0 || data.slackCheckError || (data.clientesSinPaginas?.length ?? 0) > 0 || (data.clientesSinConversacion?.length ?? 0) > 0 || (data.capacitySinRegistrar?.length ?? 0) > 0 || data.capacityCheckError) && (
        <div className="border rounded-xl p-4 bg-amber-50 border-amber-200 space-y-2 text-sm">
          <p className="font-medium text-amber-900">{t('brain.health.blindaje_title')}</p>
          <FaltanteLinea
            etiqueta={t('brain.health.no_pages', { count: data.clientesSinPaginas?.length ?? 0 })}
            items={(data.clientesSinPaginas ?? []).map((id) => BRAIN_CLIENTS[id] ?? id)}
            hint={t('brain.health.no_pages_hint')}
          />
          <FaltanteLinea
            etiqueta={t('brain.health.no_conversation', { count: data.clientesSinConversacion?.length ?? 0 })}
            items={(data.clientesSinConversacion ?? []).map((id) => BRAIN_CLIENTS[id] ?? id)}
            hint={t('brain.health.no_conversation_hint')}
          />
          <FaltanteLinea
            etiqueta={t('brain.health.capacity_sin_registrar', { count: data.capacitySinRegistrar?.length ?? 0 })}
            items={(data.capacitySinRegistrar ?? []).map((c) => `${c.name} (${Math.round(c.revenue).toLocaleString('es-AR')} USD)`)}
            hint={t('brain.health.capacity_sin_registrar_hint')}
          />
          <FaltanteLinea
            etiqueta={t('brain.health.bq_missing')}
            items={(data.bqMissing ?? []).map((id) => BRAIN_CLIENTS[id] ?? id)}
            hint={t('brain.health.bq_missing_hint')}
          />
          <FaltanteLinea
            etiqueta={t('brain.health.hubspot_missing', { count: data.hubspotMissing?.length ?? 0 })}
            items={(data.hubspotMissing ?? []).map((id) => BRAIN_CLIENTS[id] ?? id)}
            hint={t('brain.health.hubspot_missing_hint')}
          />
          {(data.bqVariantsMissing?.length ?? 0) > 0 && (
            <div className="text-amber-900">
              <span className="font-medium">{t('brain.health.bq_variants_missing')}:</span>{' '}
              {data.bqVariantsMissing.map((v) => `${BRAIN_CLIENTS[v.clientId] ?? v.clientId}: ${v.names.join(', ')}`).join(' · ')}.{' '}
              <span className="text-amber-800">{t('brain.health.bq_variants_missing_hint')}</span>
            </div>
          )}
          {data.slackNotMember.length > 0 && (
            <p className="text-amber-900">
              <span className="font-medium">{t('brain.health.slack_not_member', { count: data.slackNotMember.length })}:</span>{' '}
              {data.slackNotMember.map((c) => `#${c.channelName} (${(BRAIN_CLIENTS[c.clientId] ?? c.clientId)})`).join(', ')}. <span className="text-amber-800">{t('brain.health.slack_not_member_hint')}</span>
            </p>
          )}
          {data.slackCheckError && <p className="text-amber-800">{t('brain.health.slack_check_error', { error: data.slackCheckError })}</p>}
          {data.capacityCheckError && <p className="text-amber-800">{t('brain.health.capacity_check_error', { error: data.capacityCheckError })}</p>}
        </div>
      )}

      {/* Full sync section */}
      <div className="border rounded-xl p-4 bg-gray-50 space-y-4">
        <div>
          <p className="text-sm font-medium text-gray-700 mb-1">{t('brain.health.full_sync_title')}</p>
          <p className="text-xs text-gray-500 mb-3">
            {t('brain.health.full_sync_desc')}
          </p>
          <div className="flex flex-wrap gap-2">
            {BRAIN_SYNC_IDS.map((sync) => (
              <SyncButton key={sync} clientId="__all__" source={sync} label={t(BRAIN_SYNC_LABEL_KEYS[sync])} />
            ))}
          </div>
        </div>

        <div className="border-t pt-3">
          <p className="text-sm font-medium text-gray-700 mb-1">{t('brain.health.generate_missing_chunks_title')}</p>
          <p className="text-xs text-gray-500 mb-3">
            {t('brain.health.generate_missing_chunks_desc')}
          </p>
          <ChunkBackfillButton />
        </div>
      </div>

      {clients.length > 0 && (
        <div className="space-y-3">
          {clients.map((c) => <ClientCard key={c.client_id as string} c={c} />)}
        </div>
      )}

      <p className="text-xs text-gray-400 text-center pt-1">
        {t('brain.health.async_note')}
      </p>

      <AdminAgentPanel />
    </div>
  );
}
